'use strict';

import * as THREE from 'three';
import {isDev, isMobile, settingsData, threeJsVariables} from "../../settings";
import {ViewMode} from "../../common";
import {getWorldData, rotateToCamera} from "../controls/orbitControls";


const _lights={
    ambient      : null,
    ambientInts  : 0.1,
    ambientColor : null,
    scene1_light : null,
    scene2_light : null,
    dirLight     : null,
    target       : new THREE.Object3D(),
    sceneIndex   : 0,
}

export const directionlight = () => {
    const {scene} = threeJsVariables;
    const {directionalLight: dirConfig, ambientLight: ambientConfig, lightGroup} = config.threejs.lights;
    const isGroupOnScene = scene.getObjectByName('light_group');

    if (isGroupOnScene) {
        scene.remove(isGroupOnScene);
    }

    const lightSettings = {
        ambient: {
            color: config.threejs.lights.ambientLight?.orbit?.color || 'white',
            intensity: config.threejs.lights.ambientLight?.orbit?.intensity || 0
        },
        directional: {
            color: config.threejs.lights.directionalLight?.orbit?.color || 'white',
            intensity: config.threejs.lights.directionalLight?.orbit?.intensity || 0
        }
    }

    _lights.ambient = new THREE.AmbientLight(lightSettings.ambient.color, lightSettings.ambient.intensity);
    _lights.ambientInts = lightSettings.ambient.intensity;
    _lights.ambientColor = new THREE.Color(lightSettings.ambient.color);

    _lights.dirLight = new THREE.DirectionalLight(lightSettings.directional.color, lightSettings.directional.intensity);

    _lights.scene1_light = new THREE.DirectionalLight(lightSettings.directional.color, lightSettings.directional.intensity);
    _lights.scene2_light = new THREE.DirectionalLight( dirConfig.cylinder.color, dirConfig.cylinder.intensity);

    _lights.scene1_light.position.set(lightGroup.position.x, lightGroup.position.y, lightGroup.position.z);
    _lights.scene1_light.rotation.set(lightGroup.rotation.x, lightGroup.rotation.y, lightGroup.rotation.z);

    _lights.scene2_light.position.set(dirConfig.cylinder.position.x, dirConfig.cylinder.position.y, dirConfig.cylinder.position.z);

    _lights.dirLight.name = 'Dir. Light';
    _lights.ambient.name = 'ambientLight';

    if (isMobile) {
        _lights.scene1_light.castShadow = false;
        _lights.scene2_light.castShadow = false;
    } else {
        _lights.scene1_light.castShadow = dirConfig.orbit.shadows.castShadow;
        _lights.scene1_light.shadow.mapSize.width = dirConfig.orbit.shadows.mapSizeWidth;
        _lights.scene1_light.shadow.mapSize.height = dirConfig.orbit.shadows.mapSizeHeight;
        _lights.scene1_light.shadow.camera.near = dirConfig.orbit.shadows.near;
        _lights.scene1_light.shadow.camera.far = dirConfig.orbit.shadows.far;

        const size = 150;
        _lights.scene1_light.shadow.camera.right = size;
        _lights.scene1_light.shadow.camera.left = - size;
        _lights.scene1_light.shadow.camera.top	= size;
        _lights.scene1_light.shadow.camera.bottom = - size;


        _lights.scene2_light.castShadow = dirConfig.cylinder.shadows.castShadow;
        _lights.scene2_light.shadow.mapSize.width = dirConfig.cylinder.shadows.mapSizeWidth;
        _lights.scene2_light.shadow.mapSize.height = dirConfig.cylinder.shadows.mapSizeHeight;
        _lights.scene2_light.shadow.camera.near = dirConfig.cylinder.shadows.near;
        _lights.scene2_light.shadow.camera.far = dirConfig.cylinder.shadows.far;

        _lights.scene2_light.shadow.camera.right = size;
        _lights.scene2_light.shadow.camera.left = -size;
        _lights.scene2_light.shadow.camera.top = size;
        _lights.scene2_light.shadow.camera.bottom = -size;
    }

    const light_group = new THREE.Group();
    light_group.add( _lights.ambient, _lights.dirLight );
    light_group.name = 'light_group';
    scene.add(light_group);

    setSceneLight(0)

    //GUI
    if (isDev) {
        const dirLightHelper = new THREE.DirectionalLightHelper( _lights.dirLight, 150);
        const helperShadowMap = new THREE.CameraHelper(  _lights.dirLight.shadow.camera );
        light_group.add(dirLightHelper, helperShadowMap);

        const {gui} = threeJsVariables;

        const lightFolder = gui.addFolder('Lights options');

        const lightGroupPosition = lightFolder.addFolder('Lights Group position');
        lightGroupPosition.add(light_group.position, "x", -500, 500, 0.001);
        lightGroupPosition.add(light_group.position, "y", 0, 500, 0.001);
        lightGroupPosition.add(light_group.position, "z", -500, 500, 0.001);

        const lightGroupRotation = lightFolder.addFolder('Lights Group rotation');
        lightGroupRotation.add(light_group.rotation, "x", -7, 7, 0.001);
        lightGroupRotation.add(light_group.rotation, "y", -7, 7, 0.001);
        lightGroupRotation.add(light_group.rotation, "z", -7, 7, 0.001);

        const dirLightFolder = lightFolder.addFolder('DirectionLight');

        const dirLightPosition = dirLightFolder.addFolder('position');
        dirLightPosition.add( _lights.dirLight.position, "x", -500, 500, 0.001);
        dirLightPosition.add( _lights.dirLight.position, "y", 0, 500, 0.001);
        dirLightPosition.add( _lights.dirLight.position, "z", -500, 500, 0.001);

        const dirLightRotation = dirLightFolder.addFolder('rotation');
        dirLightRotation.add( _lights.dirLight.rotation, "x", -7, 7, 0.001);
        dirLightRotation.add( _lights.dirLight.rotation, "y", -7, 7, 0.001);
        dirLightRotation.add( _lights.dirLight.rotation, "z", -7, 7, 0.001);

        dirLightFolder.add( _lights.dirLight, 'intensity', 0, 10, 0.001);
        dirLightFolder.addColor( _lights.dirLight, 'color');

        const ambientFolder = lightFolder.addFolder('AmbientLight');
        ambientFolder.add( _lights.ambient, 'intensity', 0, 10, 0.001);
        ambientFolder.addColor(_lights.ambient, 'color');

        // gui.add(threeJsVariables.renderer, 'physicallyCorrectLights').onFinishChange(() => {
        //     console.log(threeJsVariables.renderer.physicallyCorrectLights);
        // });
    }

    updateShadowMap();
}

export function updateShadowMap () {
    if (isMobile) return;

    threeJsVariables.renderer.shadowMap.needsUpdate = true;
}

export const changeLightEnvironment = (activeMode) => {
    const dynamicLight = config.threejs.lights.dynamicLight;
    if (!dynamicLight){
        return;
    }

    const {orbitControlsVector} = getWorldData();

    if (activeMode === ViewMode.Cylinder) {

        _lights.target.position.set(0,0,0)
        _lights.target.position.add(orbitControlsVector);

        setSceneLight(1)

        lightRotation();
    } else {
        setSceneLight(0)
    }
}

export function lightRotation() {
    const dynamicLight = config.threejs.lights.dynamicLight;
    if (!dynamicLight){
        return;
    }

    const {orbitControlsVector} = getWorldData();

    if (_lights.sceneIndex===0){
        return
    }
    
    _lights.target.rotation.y = threeJsVariables.orbitControls.getAzimuthalAngle() + Math.PI/2;
    _lights.target.updateMatrixWorld();
 
    //const e = _lights.target.matrixWorld.elements;
    let v3 = new THREE.Vector3();
    v3.copy(_lights.scene2_light.position);
    v3.applyMatrix4(_lights.target.matrixWorld);

    _lights.dirLight.position.copy(v3);
    _lights.dirLight.lookAt(orbitControlsVector);
    _lights.dirLight.updateMatrixWorld();
 }

const setSceneLight = (sceneIndex)=>{
    const {renderer} = threeJsVariables;
    const {ambientLight: ambientConfig} = config.threejs.lights;

    let v = null;
    if (sceneIndex===0){
        v = _lights.scene1_light;
        renderer.shadowMap.autoUpdate = false;
        // _lights.ambient.intensity = _lights.ambientInts;
        _lights.ambient.intensity = ambientConfig.orbit.intensity;
        // _lights.ambient.color = new THREE.Color(_lights.ambientColor);
        _lights.ambient.color = new THREE.Color(ambientConfig.orbit.color);
    }else{
        v = _lights.scene2_light;
        renderer.shadowMap.autoUpdate = true;
        // _lights.ambient.intensity = 0;
        _lights.ambient.intensity = ambientConfig.cylinder.intensity;
        _lights.ambient.color = new THREE.Color(ambientConfig.cylinder.color);
    }

    _lights.dirLight.intensity = v.intensity;
    _lights.dirLight.color.copy(v.color);
    _lights.dirLight.position.copy(v.position);
    _lights.dirLight.rotation.copy(v.rotation);
    _lights.dirLight.target.position.copy(v.target.position);
    _lights.dirLight.castShadow = v.castShadow;
    _lights.dirLight.shadow = v.shadow;
    _lights.dirLight.updateMatrixWorld();
    _lights.dirLight.target.updateMatrixWorld();

    _lights.sceneIndex = sceneIndex;
}